import {
  assign,
  enqueueActions,
  fromPromise,
  setup,
  type AnyActorRef,
  type SnapshotFrom,
} from 'xstate';
import { firstValueFrom } from 'rxjs';
import isEqual from 'lodash/isEqual';
import {
  type Reservation,
  type GurneyInfo,
  type ReservationPhone,
  TripLegType,
} from '@/data/models/reservation.model';
import reservationService from '@/services/reservationService';
import reservationUtils from '@/services/reservationUtils';
import {
  reservationFormSchema,
  tripLegReservationFormSchema,
  type ReservationFormValues,
} from '../schema/reservationForm.schema';
import { filterReservationErrorsExcludingMemberPhones } from '../utils/filterReservationErrorsExcludingMemberPhones';
import {
  levelOfCareMachine,
  type LevelOfCareContext,
} from './levelOfCare.machine';
import { passengerMobilityMachine } from './passengerMobility.machine';
import { passengerAssistanceMachine } from './passengerAssistance.machine';
import { standingOrderMachine } from './standingOrder.machine';
import { appointmentDateMachine } from './appointmentDate.machine';
import { appointmentDateTimeMachine } from './appointmentDateTime.machine';
import { dropoffAddressMachine } from './dropoffAddress.machine';
import { dropoffNotesMachine } from './dropoffNotes.machine';
import { dropoffPhoneMachine } from './dropoffPhone.machine';
import { pickupAddressMachine } from './pickupAddress.machine';
import { pickupDateMachine } from './pickupDate.machine';
import { pickupDateTimeMachine } from './pickupDateTime.machine';
import { pickupNotesMachine } from './pickupNotes.machine';
import { pickupPhoneMachine } from './pickupPhone.machine';
import { reasonForTripMachine } from './reasonForTrip.machine';
import { tripTypeMachine } from './tripType.machine';
import { willCallMachine } from './willCall.machine';
import {
  hasBlockingTripLegConfidenceError,
  mergeTripLegConfidenceResponsesIntoReservationPayload,
  type TripLegConfidenceStoredEntry,
} from './tripLegConfidence';
import {
  clearTripLegScoreCache,
  runBundledValidation,
  type BundledValidationInput,
  type BundledValidationOutput,
} from './runBundledValidation';
import { clearPlaceResolveCache } from './resolveMissingPlaceIds';
import { appointmentTimeAllowedForPortalMachine } from './appointmentTimeAllowedForPortal.machine';
import { requestOverrideMachine } from './requestOverride.machine';
import {
  tripLegEstimatedPickupMachine,
  type GetDirectionsFn,
} from './tripLegEstimatedPickup.machine';
import type {
  FieldEditedValidationKey,
  ReservationFormMainContext,
  ReservationFormMainEvent,
  ReservationFormMainFeatureFlags,
  ReservationFormMainInput,
} from './reservationFormMain.types';
import {
  canReservationBeOverriddenFromContext,
  canSaveAsDraftFromContext,
  canSaveAsDraftFromEvent,
  canSaveAsDraftFromFormValues,
  canSubmitFromContext,
  canSubmitFromSubmitEvent,
} from './guards';
import { isFieldEditedWithReservation } from '../utils/isFieldEditedWithReservation';
import { shouldValidateReservation } from '../utils/shouldValidateReservation';

export type {
  ReservationFormMainContext,
  ReservationFormMainEvent,
  ReservationFormMainFeatureFlags,
  ReservationFormMainInput,
} from './reservationFormMain.types';

export { getAggregatedFormValues } from '../utils/getAggregatedFormValues';
export {
  canReservationBeOverriddenFromContext,
  canSaveAsDraftFromContext,
  canSaveAsDraftFromFormValues,
} from './guards';

/** Address selections held by the field machines (fed to place resolution). */
function getSelectedAddresses(context: ReservationFormMainContext): {
  selectedPickups: unknown[];
  selectedDropoffs: unknown[];
} {
  return {
    selectedPickups:
      (
        context.pickupAddressRef?.getSnapshot().context as
          | { values?: unknown[] }
          | undefined
      )?.values ?? [],
    selectedDropoffs:
      (
        context.dropoffAddressRef?.getSnapshot().context as
          | { values?: unknown[] }
          | undefined
      )?.values ?? [],
  };
}

/**
 * Everything a validation run depends on. The payload alone isn't enough:
 * unresolved saved addresses carry an empty struct, so two different picks
 * can share a payload and differ only in the selections resolved from.
 */
function getFieldEditedValidationKey(
  context: ReservationFormMainContext,
  event: ReservationFormMainEvent,
): FieldEditedValidationKey | undefined {
  if (!isFieldEditedWithReservation(event)) return undefined;
  return { reservation: event.reservation, ...getSelectedAddresses(context) };
}

function shouldValidateAndHasReservation({
  context,
  event,
}: {
  context: ReservationFormMainContext;
  event: ReservationFormMainEvent;
}): boolean {
  return (
    shouldValidateReservation(context, event) &&
    isFieldEditedWithReservation(event) &&
    // Same inputs as the run in flight / last applied: the server answer
    // can't change, so skip it (F1V2-6672).
    !isEqual(
      getFieldEditedValidationKey(context, event),
      context.lastFieldEditedValidationKey,
    )
  );
}

const defaultFeatureFlags: ReservationFormMainFeatureFlags = {
  addressValidationEnabled: false,
  isClientPortal: false,
};

const initialContext: ReservationFormMainContext = {
  tripTypeRef: null,
  reasonForTripRef: null,
  appointmentDateRef: null,
  appointmentDateTimeRef: null,
  pickupDateRef: null,
  pickupDateTimeRef: null,
  pickupTimeZoneRef: null,
  dropoffTimeZoneRef: null,
  pickupAddressRef: null,
  dropoffAddressRef: null,
  pickupPhoneRef: null,
  dropoffPhoneRef: null,
  pickupNotesRef: null,
  dropoffNotesRef: null,
  willCallRef: null,
  standingOrderRef: null,
  levelOfCareRef: null,
  passengerMobilityRef: null,
  passengerAssistanceRef: null,
  appointmentTimeAllowedForPortalRef: null,
  tripLegEstimatedPickupRefs: [],
  requestOverrideRef: null,
  clientBC: '',
  passengerUUID: '',
  standingOrderUUID: undefined,
  servicePlans: [],
  lastValidationResult: null,
  createdReservation: null,
  confidenceByTripIndex: {},
  errorsByTripIndex: {},
  applyTripLegValidationResultsToForm: undefined,
  getPickupTimeZoneForTrip: undefined,
  getAddressStructsForTrip: undefined,
  featureFlags: defaultFeatureFlags,
};

export const reservationFormMainMachine = setup({
  types: {
    context: {} as ReservationFormMainContext,
    events: {} as ReservationFormMainEvent,
    input: {} as ReservationFormMainInput,
  },
  actors: {
    createReservationRequest: fromPromise<
      Reservation,
      {
        clientBC: string;
        passengerUUID: string;
        passengerPhone?: ReservationPhone;
        formValues: ReservationFormValues;
        saveAsDraft: boolean;
        addressValidationEnabled: boolean;
        confidenceByTripIndex: Record<number, TripLegConfidenceStoredEntry>;
      }
    >(async ({ input }) => {
      let { reservation, standingOrder } =
        reservationUtils.getCreateReservationPayloadFromReservationForm(
          input.clientBC,
          input.passengerUUID,
          input.formValues,
          input.passengerPhone,
        );
      if (input.addressValidationEnabled) {
        reservation = mergeTripLegConfidenceResponsesIntoReservationPayload(
          reservation,
          input.confidenceByTripIndex,
        ) as Reservation;
      }
      return firstValueFrom(
        reservationService.createReservation(
          reservation,
          input.formValues.tripType !== TripLegType.MultiLeg
            ? standingOrder
            : undefined,
          input.saveAsDraft,
        ),
      );
    }),
    // Single, sequential validation: /score/trip-leg for every leg first, then
    // /validate unless a score call failed. Both calls resolve before this
    // returns so the result is applied in one transition.
    validateReservation: fromPromise<
      BundledValidationOutput,
      BundledValidationInput
    >(({ input, signal }) => runBundledValidation(input, signal)),
    tripType: tripTypeMachine,
    reasonForTrip: reasonForTripMachine,
    appointmentDate: appointmentDateMachine,
    appointmentDateTime: appointmentDateTimeMachine,
    pickupDate: pickupDateMachine,
    pickupDateTime: pickupDateTimeMachine,
    pickupAddress: pickupAddressMachine,
    dropoffAddress: dropoffAddressMachine,
    pickupPhone: pickupPhoneMachine,
    dropoffPhone: dropoffPhoneMachine,
    pickupNotes: pickupNotesMachine,
    dropoffNotes: dropoffNotesMachine,
    willCall: willCallMachine,
    standingOrder: standingOrderMachine,
    levelOfCare: levelOfCareMachine,
    passengerMobility: passengerMobilityMachine,
    passengerAssistance: passengerAssistanceMachine,
    appointmentTimeAllowedForPortal: appointmentTimeAllowedForPortalMachine,
    tripLegEstimatedPickup: tripLegEstimatedPickupMachine,
    requestOverride: requestOverrideMachine,
  },
  actions: {
    forwardOverrideOpenToDialog: ({
      context,
    }: {
      context: ReservationFormMainContext;
    }) => {
      context.requestOverrideRef?.send({ type: 'OPEN' });
    },
    syncTripLegEstimatedPickupActors: enqueueActions(
      ({ context, event, enqueue }) => {
        if (event.type !== 'SET_TRIP_COUNT') return;
        const count = Math.max(1, event.count);
        if (!context.pickupAddressRef || !context.dropoffAddressRef) return;

        for (
          let i = count;
          i < context.tripLegEstimatedPickupRefs.length;
          i++
        ) {
          enqueue.stopChild(`tripLegEstimatedPickup.${i}`);
        }

        enqueue.assign({
          tripLegEstimatedPickupRefs: ({ context, spawn }) => {
            const next = context.tripLegEstimatedPickupRefs.slice(0, count);
            while (next.length < count) {
              const idx = next.length;
              next.push(
                spawn('tripLegEstimatedPickup', {
                  id: `tripLegEstimatedPickup.${idx}`,
                  input: {
                    pickupAddressRef: context.pickupAddressRef!,
                    dropoffAddressRef: context.dropoffAddressRef!,
                    tripIndex: idx,
                    getDirections: context.getDirections,
                    appointmentDateTimeRef:
                      context.appointmentDateTimeRef ?? undefined,
                    getPickupTimeZone: () =>
                      context.getPickupTimeZoneForTrip?.(idx),
                    getAddressStructs: (tripIndex) =>
                      context.getAddressStructsForTrip?.(tripIndex) ?? {},
                  },
                }),
              );
            }
            return next;
          },
        });
      },
    ),
    syncTripCount: ({
      context,
      event,
    }: {
      context: ReservationFormMainContext;
      event: ReservationFormMainEvent;
    }) => {
      if (event.type !== 'SET_TRIP_COUNT') return;
      const count = Math.max(1, event.count);
      const refs: (AnyActorRef | null)[] = [
        context.reasonForTripRef,
        context.appointmentDateRef,
        context.appointmentDateTimeRef,
        context.pickupDateRef,
        context.pickupDateTimeRef,
        context.pickupTimeZoneRef,
        context.dropoffTimeZoneRef,
        context.pickupAddressRef,
        context.dropoffAddressRef,
        context.pickupPhoneRef,
        context.dropoffPhoneRef,
        context.pickupNotesRef,
        context.dropoffNotesRef,
        context.willCallRef,
        context.standingOrderRef,
        context.levelOfCareRef,
        context.passengerMobilityRef,
        context.passengerAssistanceRef,
      ];
      refs.forEach((r) => r?.send({ type: 'RESIZE', count }));
    },
    resetAllFields: ({ context }: { context: ReservationFormMainContext }) => {
      context.tripTypeRef?.send({ type: 'RESET' });
      context.reasonForTripRef?.send({ type: 'RESET' });
      const tripFieldRefs: (AnyActorRef | null)[] = [
        context.reasonForTripRef,
        context.appointmentDateRef,
        context.appointmentDateTimeRef,
        context.pickupDateRef,
        context.pickupDateTimeRef,
        context.pickupTimeZoneRef,
        context.dropoffTimeZoneRef,
        context.pickupAddressRef,
        context.dropoffAddressRef,
        context.pickupPhoneRef,
        context.dropoffPhoneRef,
        context.pickupNotesRef,
        context.dropoffNotesRef,
        context.willCallRef,
        context.standingOrderRef,
        context.passengerMobilityRef,
        context.passengerAssistanceRef,
      ];
      tripFieldRefs.forEach((r) => r?.send({ type: 'RESET' }));
      context.levelOfCareRef?.send({ type: 'RESET' });
      context.appointmentTimeAllowedForPortalRef?.send({ type: 'RESET' });
      context.tripLegEstimatedPickupRefs.forEach((r) =>
        r.send({ type: 'RESET' }),
      );
      clearTripLegScoreCache();
      clearPlaceResolveCache();
    },
    recordFieldEditedValidationKey: assign({
      lastFieldEditedValidationKey: ({ context, event }) =>
        getFieldEditedValidationKey(context, event),
    }),
    clearFieldEditedValidationKey: assign({
      lastFieldEditedValidationKey: () => undefined,
    }),
    updateFormValuesFromValidationResult: ({ context, event }) => {
      if (!('output' in event)) return;
      const output = event.output as BundledValidationOutput;
      const validations = output?.validations;
      if (validations == null) return;
      // Pass the whole output: the applier surfaces /validate field errors AND
      // /score trip-leg errors (output.errorsByTripIndex) in one pass.
      context.applyTripLegValidationResultsToForm?.()?.(output);
      for (const update of validations.tripLegUpdates) {
        const idx = update.tripLegSequence - 1;
        const trip = context.submitFormValues?.trips[idx];
        if (trip == null) continue;
        if (update.pickUpAddress?.timeZone) {
          trip.pickupTimeZone = update.pickUpAddress.timeZone;
        }
        if (update.dropOffAddress?.timeZone) {
          trip.dropoffTimeZone = update.dropOffAddress.timeZone;
        }
      }
    },
  },
  guards: {
    canSubmit: ({ context }) => canSubmitFromContext(context),
    canSubmitFromSubmitEvent,
    canSaveAsDraftFromEvent,
    canSaveAsDraft: ({ context }) => canSaveAsDraftFromContext(context),
    canReservationBeOverridden: ({ context }) =>
      canReservationBeOverriddenFromContext(context),
    shouldValidateAndHasReservation,
  },
}).createMachine({
  id: 'reservationFormMain',
  context: ({ input }) => ({
    ...initialContext,
    clientBC: input?.clientBC ?? '',
    passengerUUID: input?.passengerUUID ?? '',
    standingOrderUUID: input?.standingOrderUUID,
    passengerPhone: input?.passengerPhone,
    getDirections: input?.getDirections,
    resolvePlace: input?.resolvePlace,
    applyTripLegValidationResultsToForm:
      input?.applyTripLegValidationResultsToForm,
    getPickupTimeZoneForTrip: input?.getPickupTimeZoneForTrip,
    getAddressStructsForTrip: input?.getAddressStructsForTrip,
    featureFlags: input?.featureFlags ?? defaultFeatureFlags,
  }),
  initial: 'bootstrap',
  states: {
    bootstrap: {
      entry: assign(({ spawn, context }) => {
        const pickupAddressRef = spawn('pickupAddress', {
          id: 'pickupAddress',
        });
        const dropoffAddressRef = spawn('dropoffAddress', {
          id: 'dropoffAddress',
        });
        const tripTypeRef = spawn('tripType', { id: 'tripType' });
        const reasonForTripRef = spawn('reasonForTrip', {
          id: 'reasonForTrip',
        });
        const appointmentDateRef = spawn('appointmentDate', {
          id: 'appointmentDate',
        });
        const appointmentDateTimeRef = spawn('appointmentDateTime', {
          id: 'appointmentDateTime',
        });
        const pickupDateRef = spawn('pickupDate', { id: 'pickupDate' });
        const pickupDateTimeRef = spawn('pickupDateTime', {
          id: 'pickupDateTime',
        });
        const pickupPhoneRef = spawn('pickupPhone', { id: 'pickupPhone' });
        const dropoffPhoneRef = spawn('dropoffPhone', { id: 'dropoffPhone' });
        const pickupNotesRef = spawn('pickupNotes', { id: 'pickupNotes' });
        const dropoffNotesRef = spawn('dropoffNotes', { id: 'dropoffNotes' });
        const willCallRef = spawn('willCall', { id: 'willCall' });
        const standingOrderRef = spawn('standingOrder', {
          id: 'standingOrder',
        });
        const levelOfCareRef = spawn('levelOfCare', { id: 'levelOfCare' });
        const passengerMobilityRef = spawn('passengerMobility', {
          id: 'passengerMobility',
        });
        const passengerAssistanceRef = spawn('passengerAssistance', {
          id: 'passengerAssistance',
        });
        const appointmentTimeAllowedForPortalRef = spawn(
          'appointmentTimeAllowedForPortal',
          {
            id: 'appointmentTimeAllowedForPortal',
            input: { appointmentDateTimeRef },
          },
        );
        const tripLegEstimatedPickupRefs = [
          spawn('tripLegEstimatedPickup', {
            id: 'tripLegEstimatedPickup.0',
            input: {
              pickupAddressRef,
              dropoffAddressRef,
              tripIndex: 0,
              getDirections: context.getDirections,
              appointmentDateTimeRef,
              getPickupTimeZone: () => context.getPickupTimeZoneForTrip?.(0),
              getAddressStructs: (tripIndex) =>
                context.getAddressStructsForTrip?.(tripIndex) ?? {},
            },
          }),
        ];
        const requestOverrideRef = spawn('requestOverride', {
          id: 'requestOverride',
        });
        return {
          tripTypeRef,
          reasonForTripRef,
          appointmentDateRef,
          appointmentDateTimeRef,
          pickupDateRef,
          pickupDateTimeRef,
          pickupAddressRef,
          dropoffAddressRef,
          pickupPhoneRef,
          dropoffPhoneRef,
          pickupNotesRef,
          dropoffNotesRef,
          willCallRef,
          standingOrderRef,
          levelOfCareRef,
          passengerMobilityRef,
          passengerAssistanceRef,
          appointmentTimeAllowedForPortalRef,
          tripLegEstimatedPickupRefs,
          requestOverrideRef,
        };
      }),
      always: { target: 'editing' },
    },
    editing: {
      on: {
        FIELD_EDITED: {
          target: 'validating',
          guard: 'shouldValidateAndHasReservation',
          actions: 'recordFieldEditedValidationKey',
        },
        SUBMIT: {
          target: 'submitting',
          guard: 'canSubmitFromSubmitEvent',
          actions: assign({
            submitFormValues: ({ event }) =>
              event.type === 'SUBMIT' ? event.formValues : undefined,
            saveAsDraft: ({ event }) =>
              event.type === 'SUBMIT' ? false : undefined,
          }),
        },
        SAVE_AS_DRAFT: {
          target: 'submitting',
          guard: 'canSaveAsDraftFromEvent',
          actions: assign({
            submitFormValues: ({ event }) =>
              event.type === 'SAVE_AS_DRAFT' ? event.formValues : undefined,
            saveAsDraft: ({ event }) =>
              event.type === 'SAVE_AS_DRAFT' ? true : undefined,
          }),
        },
        RESET_ALL: {
          actions: [
            'resetAllFields',
            'clearFieldEditedValidationKey',
            assign({
              tripLegMergedValues: () => undefined,
              confidenceByTripIndex: () => ({}),
              errorsByTripIndex: () => ({}),
            }),
          ],
        },
        OVERRIDE_OPEN: {
          guard: 'canReservationBeOverridden',
          actions: 'forwardOverrideOpenToDialog',
        },
      },
    },
    validating: {
      on: {
        OVERRIDE_OPEN: {
          guard: 'canReservationBeOverridden',
          actions: 'forwardOverrideOpenToDialog',
        },
        FIELD_EDITED: {
          target: 'validating',
          guard: 'shouldValidateAndHasReservation',
          reenter: true,
          actions: 'recordFieldEditedValidationKey',
        },
      },
      invoke: {
        id: 'validateReservation',
        src: 'validateReservation',
        input: ({ context, event }): BundledValidationInput => {
          const addressValidationEnabled =
            context.featureFlags.addressValidationEnabled;
          const { selectedPickups, selectedDropoffs } =
            getSelectedAddresses(context);
          if (!isFieldEditedWithReservation(event)) {
            return {
              reservation: null,
              addressValidationEnabled,
              resolvePlace: context.resolvePlace,
              selectedPickups,
              selectedDropoffs,
            };
          }
          return {
            reservation: event.reservation,
            addressValidationEnabled,
            resolvePlace: context.resolvePlace,
            selectedPickups,
            selectedDropoffs,
          };
        },
        onDone: {
          target: 'editing',
          actions: [
            'updateFormValuesFromValidationResult',
            assign({
              lastValidationResult: ({ event }) => event.output.validations,
              confidenceByTripIndex: ({ event }) =>
                event.output.confidenceByTripIndex,
              errorsByTripIndex: ({ event }) => event.output.errorsByTripIndex,
            }),
          ],
        },
        // Forget the failed payload so the same values can be retried.
        onError: {
          target: 'editing',
          actions: 'clearFieldEditedValidationKey',
        },
      },
    },
    submitting: {
      invoke: {
        id: 'createReservationRequest',
        src: 'createReservationRequest',
        input: ({ context }) => ({
          clientBC: context.clientBC,
          passengerUUID: context.passengerUUID,
          passengerPhone: context.passengerPhone,
          formValues: context.submitFormValues!,
          saveAsDraft: context.saveAsDraft ?? false,
          addressValidationEnabled:
            context.featureFlags.addressValidationEnabled,
          confidenceByTripIndex: context.confidenceByTripIndex,
        }),
        onDone: {
          target: 'success',
          actions: assign({
            createdReservation: ({ event }) => event.output,
          }),
        },
        onError: { target: 'editing' },
      },
    },
    success: {
      on: {
        RESET_ALL: {
          target: 'editing',
          actions: [
            'resetAllFields',
            'clearFieldEditedValidationKey',
            assign({
              lastValidationResult: () => null,
              tripLegMergedValues: () => undefined,
              confidenceByTripIndex: () => ({}),
              errorsByTripIndex: () => ({}),
            }),
          ],
        },
      },
    },
  },
  on: {
    CLEAR_LAST_FIELD_EDITED_VALIDATION_KEY: {
      actions: 'clearFieldEditedValidationKey',
    },
    SET_TRIP_COUNT: {
      actions: ['syncTripCount', 'syncTripLegEstimatedPickupActors'],
    },
    SET_PASSENGER: {
      actions: assign({
        clientBC: ({ event }) =>
          event.type === 'SET_PASSENGER' ? event.clientBC : '',
        passengerUUID: ({ event }) =>
          event.type === 'SET_PASSENGER' ? event.passengerUUID : '',
        passengerPhone: ({ event }) =>
          event.type === 'SET_PASSENGER' ? event.passengerPhone : undefined,
        standingOrderUUID: ({ event }) =>
          event.type === 'SET_PASSENGER' ? event.standingOrderUUID : undefined,
      }),
    },
    SET_PORTAL_NAME: {
      actions: ({ context, event }) => {
        if (event.type !== 'SET_PORTAL_NAME') return;
        context.appointmentTimeAllowedForPortalRef?.send({
          type: 'SET_PORTAL_NAME',
          portalName: event.portalName,
        });
      },
    },
    TRIP_LEG_MERGED_VALUES: {
      actions: assign({
        tripLegMergedValues: ({ event }) =>
          event.type === 'TRIP_LEG_MERGED_VALUES'
            ? event.mergedValues
            : undefined,
      }),
    },
  },
});

export type TripLegNextButtonDisabledOptions = {
  isMemberPortal?: boolean;
  hasCareLevelMismatch?: boolean;
};

/*
 * Core logic for determining if the Next button on the Trip Leg form should be disabled.
 */
export function selectTripLegNextButtonDisabled(
  snapshot: SnapshotFrom<typeof reservationFormMainMachine>,
  options?: TripLegNextButtonDisabledOptions,
): boolean {
  const isBusy =
    snapshot.matches('submitting') || snapshot.matches('validating');

  const merged = snapshot.context.tripLegMergedValues;
  if (merged === undefined) {
    return true;
  }

  // Disable the next button if gurney is selected and there is no approval
  const levelOfCareRef = snapshot.context.levelOfCareRef;
  const isGurneySelected =
    levelOfCareRef != null
      ? ((
          levelOfCareRef.getSnapshot().context as LevelOfCareContext
        ).isGurneySelected.some(Boolean) ?? false)
      : false;
  const isOverridden = Boolean(merged.AllowOverride);
  if (isGurneySelected && !isOverridden) {
    return true;
  }

  // Disable the next button if we are in a member portal and there
  // is a care level mismatch
  const isMemberPortal = Boolean(options?.isMemberPortal);
  const hasCareLevelMismatch = Boolean(options?.hasCareLevelMismatch);
  if (isMemberPortal && hasCareLevelMismatch) {
    return true;
  }

  const isSchemaValid = tripLegReservationFormSchema.safeParse(merged).success;
  const hasBlockingValidationErrors =
    filterReservationErrorsExcludingMemberPhones(
      snapshot.context.lastValidationResult?.errors ?? [],
    ).length > 0;
  const isFormOverridden = Boolean(merged.AllowOverride);

  // Trip-leg confidence errors aren't among /validate's errors, so gate on
  // them explicitly (warnings excluded).
  const hasBlockingTripLegErrors = hasBlockingTripLegConfidenceError(
    snapshot.context.errorsByTripIndex,
    snapshot.context.featureFlags.isClientPortal,
  );

  return (
    isBusy ||
    !isSchemaValid ||
    hasBlockingTripLegErrors ||
    (hasBlockingValidationErrors && !isFormOverridden)
  );
}

export type EditTripLegDoneButtonDisabledOptions = {
  isFormHydrated?: boolean;
  isMemberPortal?: boolean;
  hasCareLevelMismatch?: boolean;
  /** Trip-details only (e.g. Add Trip Leg dialog without member form). */
  tripLegOnly?: boolean;
};

/*
 * Edit Trip Leg dialog Done button — full-form Zod validity plus outstanding
 * server errors: trip-leg (/score/trip-leg) confidence errors and blocking
 * /validate errors (unless overridden). Legacy TripLegDialog blocks both via
 * its manual form errors + isOverrideable/isOverridden.
 */
export function selectEditTripLegDoneButtonDisabled(
  snapshot: SnapshotFrom<typeof reservationFormMainMachine>,
  options?: EditTripLegDoneButtonDisabledOptions,
): boolean {
  if (!options?.isFormHydrated) {
    return true;
  }

  if (snapshot.matches('submitting')) {
    return true;
  }

  const merged = snapshot.context.tripLegMergedValues;
  if (merged === undefined) {
    return true;
  }

  const tripLegCount = merged.trips?.length ?? 0;
  const levelOfCareRef = snapshot.context.levelOfCareRef;
  const isGurneySelected =
    levelOfCareRef != null
      ? (
          levelOfCareRef.getSnapshot().context as LevelOfCareContext
        ).isGurneySelected
          .slice(0, tripLegCount)
          .some(Boolean)
      : false;

  if (isGurneySelected && !merged.AllowOverride) {
    return true;
  }

  const isMemberPortal = Boolean(options?.isMemberPortal);
  const hasCareLevelMismatch = Boolean(options?.hasCareLevelMismatch);
  if (isMemberPortal && hasCareLevelMismatch) {
    return true;
  }

  // An outstanding trip-leg (/score/trip-leg) confidence error isn't visible
  // to the Zod parse below (it's a server error), so gate on it explicitly —
  // same as selectTripLegNextButtonDisabled. Client-portal low
  // address-confidence warnings are not blocking.
  const isClientPortal = snapshot.context.featureFlags.isClientPortal;
  if (
    hasBlockingTripLegConfidenceError(
      snapshot.context.errorsByTripIndex,
      isClientPortal,
    )
  ) {
    return true;
  }

  // Blocking /validate errors gate Done as well. Same clause as
  // selectTripLegNextButtonDisabled; an approved override lifts it (legacy
  // TripLegDialog's isOverrideable/isOverridden semantics).
  const hasBlockingValidationErrors =
    filterReservationErrorsExcludingMemberPhones(
      snapshot.context.lastValidationResult?.errors ?? [],
    ).length > 0;
  if (hasBlockingValidationErrors && !merged.AllowOverride) {
    return true;
  }

  const schema = options?.tripLegOnly
    ? tripLegReservationFormSchema
    : reservationFormSchema;
  const mergedForValidation = options?.tripLegOnly
    ? {
        ...merged,
        trips: merged.trips?.map((trip) => ({
          ...trip,
          tripLegType: merged.tripType,
        })),
      }
    : merged;

  const missingPickup = (mergedForValidation.trips ?? []).some(
    (trip) => !(trip.pickupDateTime || trip.isWillCall),
  );
  if (missingPickup) {
    return true;
  }

  return !schema.safeParse(mergedForValidation).success;
}

/*
 * Gurney payload for save/update APIs. Uses level-of-care machine state for
 * gurney selection and attribute levels; height/weight come from form trips.
 */
export function getGurneyDataFromReservationFormSnapshot(
  snapshot: SnapshotFrom<typeof reservationFormMainMachine>,
  formValues: ReservationFormValues,
): GurneyInfo[] | undefined {
  const levelOfCareRef = snapshot.context.levelOfCareRef;
  if (levelOfCareRef == null) {
    return undefined;
  }

  const locCtx = levelOfCareRef.getSnapshot().context as LevelOfCareContext;
  const tripCount = formValues.trips.length;
  if (!locCtx.isGurneySelected.slice(0, tripCount).some(Boolean)) {
    return undefined;
  }

  return formValues.trips.map((trip, index) => ({
    gurneyTypeTransportationAttributeUUID:
      locCtx.attributeLevel2Values[index] || trip.AttributeLevel2,
    memberHeightInches: parseInt(String(trip.height ?? ''), 10),
    memberWeightLbs: parseInt(String(trip.weight ?? ''), 10),
    oxygenCapacityTransportationAttributeUUID:
      locCtx.attributeLevel4Values[index] || trip.AttributeLevel4 || undefined,
    stairsCountTransportationAttributeUUID:
      locCtx.attributeLevel5Values[index] || trip.AttributeLevel5 || undefined,
    waitAndReturn: false,
  }));
}

/*
 * Core logic for determining if the Request Override button should be disabled.
 */
export function selectRequestOverrideButtonDisabled(
  snapshot: SnapshotFrom<typeof reservationFormMainMachine>,
): boolean {
  const isBusy =
    snapshot.matches('submitting') || snapshot.matches('validating');

  const merged = snapshot.context.tripLegMergedValues;
  if (merged === undefined) {
    return true;
  }

  const isFormOverridden = Boolean(merged.AllowOverride);

  return (
    isBusy ||
    isFormOverridden ||
    !canReservationBeOverriddenFromContext(snapshot.context)
  );
}

/*
 * Core logic for determining if the Save As Draft button should be disabled.
 */
export function selectSaveAsDraftButtonDisabled(
  snapshot: SnapshotFrom<typeof reservationFormMainMachine>,
): boolean {
  const isBusy =
    snapshot.matches('submitting') || snapshot.matches('validating');

  const merged = snapshot.context.tripLegMergedValues;
  if (merged === undefined) {
    return true;
  }

  return isBusy || !canSaveAsDraftFromFormValues(merged);
}
